import type { ApiInvite } from '#/api/invite/types/ApiInvite';
import { Box } from '#/components/Box/Box';
import { Divider } from '#/components/Divider/Divider';
import type { FormUsageContext } from '#/components/Form/Form';
import { Form } from '#/components/Form/Form';
import type {
  FormMapErrorFunction,
  FormSubmitFunction,
} from '#/components/Form/FormContext';
import { GeneralError } from '#/components/Form/GeneralError';
import { InputField } from '#/components/Form/InputField';
import { LocationField } from '#/components/Form/LocationField';
import { RecaptchaField } from '#/components/Form/RecaptchaField';
import { SubmitButton } from '#/components/Form/SubmitButton/SubmitButton';
import { InviteMessage } from '#/components/InviteMessage/InviteMessage';
import { StyledNativeLinkWithRef } from '#/components/Link/StyledNativeLinkWithRef';
import { AppleOAuthButton } from '#/components/OAuthButton/AppleOAuthButton';
import { FacebookOAuthButton } from '#/components/OAuthButton/FacebookOAuthButton';
import { GoogleOAuthButton } from '#/components/OAuthButton/GoogleOAuthButton';
import { BodySmall } from '#/components/Text/BodySmall';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { SearchLocation } from '#/modules/search/types/SearchLocation';

import { SignupFormLoginLink } from './SignupFormLoginLink';

// TODO: add proper types to SignupFormValues
export type SignupFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  location: SearchLocation | null;
  recaptchaKey: number;
  gRecaptchaResponse: unknown;
};

type Props = {
  analyticsTitle: string;
  usageContext: FormUsageContext;
  initialValues: Partial<SignupFormValues>;
  invite?: ApiInvite | null | undefined;
  showLoginModal?: (email: string) => void;
  getLoginUrlAndState: (email?: string) => {
    to: string;
    state: Record<string, unknown>;
  };
  getThirdPartyLoginUrl: (
    provider: 'facebook' | 'google' | 'apple',
  ) => Promise<string> | string;
  onSubmit: FormSubmitFunction<SignupFormValues>;
  userCameFromMembershipPage: boolean;
};

export function SignupForm({
  analyticsTitle,
  usageContext,
  initialValues,
  invite,
  showLoginModal,
  getLoginUrlAndState,
  getThirdPartyLoginUrl,
  onSubmit,
  userCameFromMembershipPage,
}: Props) {
  const { i18n } = useI18n();

  const emailErrorWithLogin = (email: string) =>
    i18n('Email already in use - Please {{link}}login{{/link}}', {
      link: (
        <SignupFormLoginLink
          formUsageContext={usageContext}
          email={email}
          getLoginUrlAndState={getLoginUrlAndState}
          showLoginModal={showLoginModal}
          qaId="email-error-login-link"
        />
      ),
    });

  const mapError: FormMapErrorFunction<SignupFormValues> = ({
    name,
    value,
    type,
    description,
  }) =>
    name === 'email' && type === 'alreadyInUse'
      ? emailErrorWithLogin(typeof value === 'string' ? value : '')
      : description;

  return (
    <Form
      analyticsTitle={analyticsTitle}
      usageContext={usageContext}
      qaId="signup-form"
      initialValues={initialValues}
      mapError={mapError}
      onSubmit={onSubmit}
      render={({ values }) => (
        <>
          {invite && (
            <BodySmall mt={3} mb={4}>
              {invite.hooks.map((hook) => (
                <InviteMessage
                  key={hook.hookType}
                  invite={invite}
                  hook={hook}
                />
              ))}
            </BodySmall>
          )}

          {!userCameFromMembershipPage && (
            <Box mb={[16, null, 16]}>
              <BodySmall textAlign="center">
                {i18n('Already have an account?')}{' '}
                <SignupFormLoginLink
                  qaId="signup-form-login-link"
                  email={values.email}
                  getLoginUrlAndState={getLoginUrlAndState}
                  showLoginModal={showLoginModal}
                  formUsageContext={usageContext}
                >
                  {i18n('Log In')}
                </SignupFormLoginLink>
              </BodySmall>
            </Box>
          )}

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <GoogleOAuthButton getUrl={() => getThirdPartyLoginUrl('google')} />
            <FacebookOAuthButton
              getUrl={() => getThirdPartyLoginUrl('facebook')}
            />
            <AppleOAuthButton getUrl={() => getThirdPartyLoginUrl('apple')} />
          </div>

          <Divider marginTop={24} marginBottom={24} />

          <GeneralError />

          {!userCameFromMembershipPage && (
            <Box display="flex" flexWrap="wrap" mx={-12}>
              <Box px={12} width={[1, 1 / 2]} flex="0 0 auto">
                <InputField
                  dataModalFocus={usageContext === 'modal' ? 'true' : undefined}
                  name="firstName"
                  label={i18n('First Name')}
                  placeholder={i18n('Casey')}
                />
              </Box>

              <Box px={12} width={[1, 1 / 2]} flex="0 0 auto">
                <InputField
                  name="lastName"
                  label={i18n('Last Name')}
                  placeholder={i18n('Smith')}
                />
              </Box>
            </Box>
          )}

          {!userCameFromMembershipPage && (
            <InputField
              name="email"
              type="email"
              label={i18n('Email')}
              placeholder={i18n('casey.smith@example.com')}
            />
          )}

          <InputField
            name="password"
            type="password"
            label={i18n('Password')}
            placeholder={i18n('Password')}
          />

          {!userCameFromMembershipPage && (
            <LocationField
              name="location"
              label={
                /* TRANSLATORS: where is the user located? e.g. New York */
                i18n('Location')
              }
              customErrorMatch={(errors) =>
                errors?.latitude ?? errors?.longitude
              }
            />
          )}

          <RecaptchaField />

          <Box mt={[16, null, 40]} display="flex" justifyContent="flex-end">
            <SubmitButton fullWidth size="large">
              {i18n('Sign Up')}
            </SubmitButton>
          </Box>

          <Box mt={[16, null, 24]} fontSize="16px">
            {i18n(
              'By continuing, you agree to Idealist’s {{terms}}Terms of Service{{/terms}}, and acknowledge our {{privacy}}Privacy Policy{{/privacy}}.',
              {
                terms: (
                  <StyledNativeLinkWithRef
                    href={i18n('https://www.idealist.org/en/terms-of-service')}
                    target="_blank"
                  ></StyledNativeLinkWithRef>
                ),
                privacy: (
                  <StyledNativeLinkWithRef
                    href={i18n('https://www.idealist.org/en/privacy-policy')}
                    target="_blank"
                  ></StyledNativeLinkWithRef>
                ),
              },
            )}
          </Box>
        </>
      )}
    />
  );
}
